// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";
import React, { useState } from "react";

import {
  RCAF_LOW_UPPER_THRESHOLD_V1,
  RCAF_MEDIUM_UPPER_THRESHOLD_V1,
  rcafAssessmentQuestions,
} from "~datatypes";

import { Opportunity } from "../../../../WorkflowsStore";
import { UsTnReclassification2026Form } from "../../../../WorkflowsStore/Opportunity/Forms/UsTnReclassification2026Form";
import { Resident } from "../../../../WorkflowsStore/Resident";
import { FileGeneratorArgs, renderMultipleDocx } from "../../DOCXFormGenerator";
import { FormContainer } from "../../FormContainer";
import FormViewer from "../../FormViewer";
import { useOpportunityFormContext } from "../../OpportunityFormContext";
import { PrintablePage } from "../../styles";
import { downloadZipFile } from "../../utils";
import {
  AGE_SUPPORTING_TEXT,
  BLOCKED_DOWNLOAD_WRONG_VERSION_OLD,
  ClassificationFormPage,
  ClassificationResourceLinksBanner,
  DISCIPLINARY_RECORD_SUPPORTING_TEXT,
  DoubleNotes,
  Header,
  PROGRAM_COMPLETION_SUPPORTING_TEXT,
  Q1_SUPPORTING_TEXT,
  Q2_SUPPORTING_TEXT,
  TextboxWithHeader,
  TotalScore,
} from "../common/Classification2026";
import classificationNextSteps2026Template from "../common/Classification2026/classification_next_steps_2026.docx";
import { PostDownloadModal } from "../common/Classification2026/NextStepsModal";
import {
  getTrusteeTemplateArgs,
  TrusteeChecklist,
} from "../common/Classification2026/TrusteeChecklist";
import { useTrusteeChecklistState } from "../common/Classification2026/useTrusteeChecklistState";
import {
  RCAF_V2_CUTOFF_DATE,
  reclassificationBlockedDownloadTooltip,
} from "../common/Classification2026/utils";
import { PreworkModal } from "../common/preworkModal";
import { ScoredAssessmentQuestion } from "../common/ScoredAssessmentQuestion";
import CoverSheet from "../CustodyReclassification/CoverSheet";
import HearingNotice from "../CustodyReclassification/HearingNotice";
import { getCoverSheetTemplateArgs } from "../CustodyReclassification/utils";
import rcafTemplate from "./rcaf_template.docx";

export const FormUsTnReclassification2026 = observer(
  function FormUsTnReclassification2026({
    opportunity,
  }: {
    opportunity: Opportunity;
  }) {
    const formRef = React.useRef<HTMLDivElement>(null);
    const [postDownloadModalIsOpen, setPostDownloadModalIsOpen] =
      useState<boolean>(false);
    const form = useOpportunityFormContext() as UsTnReclassification2026Form;
    const { derivedData, formTemplateData, formData } = form;
    const resident = opportunity.person as Resident;

    const {
      reworkEnabled,
      includeTrusteeChecklist,
      trusteeAssessmentRequired,
    } = useTrusteeChecklistState(derivedData.totalText, formData);

    const onClickDownload = async () => {
      const fileInputs: FileGeneratorArgs[] = [
        [
          `${resident.displayName} - Classification Next Steps.docx`,
          classificationNextSteps2026Template,
          formTemplateData,
        ],
        [
          `${resident.displayName} - Reclassification Form.docx`,
          rcafTemplate,
          {
            ...getCoverSheetTemplateArgs(resident, formData, reworkEnabled),
            ...formTemplateData,
          },
        ],
      ];

      if (includeTrusteeChecklist) {
        fileInputs.push(getTrusteeTemplateArgs(resident, form, reworkEnabled));
      }

      const documents = await renderMultipleDocx(fileInputs);

      downloadZipFile(
        `${resident.displayName} - Classification Packet 2026.zip`,
        documents,
      );

      setPostDownloadModalIsOpen(true);
    };

    const wrongFormVersion =
      formData.date === undefined ||
      new Date(formData.date) >= RCAF_V2_CUTOFF_DATE;

    const downloadTooltip = reclassificationBlockedDownloadTooltip({
      totalScore: derivedData.totalScore,
      hearingDate: formData.hearingDate,
      formData,
      trusteeAssessmentRequired,
      wrongVersionTooltip: wrongFormVersion
        ? BLOCKED_DOWNLOAD_WRONG_VERSION_OLD
        : undefined,
    });

    return (
      <FormContainer
        heading="RCAF"
        agencyName="TDOC"
        onClickDownload={() => onClickDownload()}
        opportunity={opportunity}
        isMissingContent={downloadTooltip !== undefined}
        downloadTooltip={downloadTooltip}
        downloadButtonLabel="Download as .DOCX"
        headerBanner={<ClassificationResourceLinksBanner />}
      >
        <PreworkModal />
        <FormViewer formRef={formRef}>
          <CoverSheet />
          <PrintablePage stretchable>
            <ClassificationFormPage>
              <Header>
                TENNESSEE CLASSIFICATION INSTRUMENT: RECLASSIFICATION
              </Header>
              <ScoredAssessmentQuestion
                questionSpec={rcafAssessmentQuestions[0]}
                questionNumber={1}
                supportingText={Q1_SUPPORTING_TEXT}
              >
                <DoubleNotes>
                  <TextboxWithHeader
                    header={
                      "List prior violent/assaultive felony convictions (TDOC) in Last 60 Months (imposed date, charge):"
                    }
                    name={"q1aNotes"}
                  />
                  <TextboxWithHeader
                    header={
                      "List prior ISC or Diversion convictions in  the Last 60 Months - Please confirm if they are violent/assaultive felony convictions (imposed date, charge):"
                    }
                    name={"q1bNotes"}
                  />
                </DoubleNotes>
              </ScoredAssessmentQuestion>
              <ScoredAssessmentQuestion
                questionSpec={rcafAssessmentQuestions[1]}
                questionNumber={2}
                supportingText={Q2_SUPPORTING_TEXT}
              >
                <TextboxWithHeader
                  header={"List current offenses:"}
                  name={"q2Notes"}
                />
              </ScoredAssessmentQuestion>
              <ScoredAssessmentQuestion
                questionSpec={rcafAssessmentQuestions[2]}
                questionNumber={3}
                supportingText={DISCIPLINARY_RECORD_SUPPORTING_TEXT}
              >
                <TextboxWithHeader
                  header={"List disciplinaries:"}
                  name={"q3NotesFormatted"}
                />
              </ScoredAssessmentQuestion>
              <ScoredAssessmentQuestion
                questionSpec={rcafAssessmentQuestions[3]}
                questionNumber={4}
                supportingText={DISCIPLINARY_RECORD_SUPPORTING_TEXT}
              >
                <TextboxWithHeader
                  header={"List disciplinaries:"}
                  name={"q4NotesFormatted"}
                />
              </ScoredAssessmentQuestion>
            </ClassificationFormPage>
          </PrintablePage>
          <PrintablePage stretchable>
            <ClassificationFormPage>
              <ScoredAssessmentQuestion
                questionSpec={rcafAssessmentQuestions[4]}
                questionNumber={5}
                supportingText={DISCIPLINARY_RECORD_SUPPORTING_TEXT}
              >
                <TextboxWithHeader
                  header={"List disciplinaries:"}
                  name={"q5NotesFormatted"}
                />
              </ScoredAssessmentQuestion>
              <ScoredAssessmentQuestion
                questionSpec={rcafAssessmentQuestions[5]}
                questionNumber={6}
                supportingText={AGE_SUPPORTING_TEXT}
              />
              <ScoredAssessmentQuestion
                questionSpec={rcafAssessmentQuestions[6]}
                questionNumber={7}
                supportingText={PROGRAM_COMPLETION_SUPPORTING_TEXT}
              >
                <TextboxWithHeader
                  header={"List completed programs:"}
                  name={"q7Notes"}
                />
              </ScoredAssessmentQuestion>
              <TotalScore
                score={derivedData.totalScore}
                lowUpper={RCAF_LOW_UPPER_THRESHOLD_V1}
                mediumUpper={RCAF_MEDIUM_UPPER_THRESHOLD_V1}
                trusteeAssessmentRequired={trusteeAssessmentRequired}
                reworkEnabled={reworkEnabled}
                custodyLevel={derivedData.totalText}
              />
            </ClassificationFormPage>
          </PrintablePage>
          <TrusteeChecklist display={includeTrusteeChecklist} />
          <HearingNotice pilotVersion />
        </FormViewer>
        <PostDownloadModal
          isOpen={postDownloadModalIsOpen}
          onClose={() => setPostDownloadModalIsOpen(false)}
        />
      </FormContainer>
    );
  },
);
